import { useMemo } from 'react';
import type { BespokeFormSectionProps } from '../bespokeRegistry';
import { BespokeSectionProvider, buildSectionSchemaMap } from '../primitives';
import { computeLayoutDerivedTotals } from './deriveLayoutTotals';
import { ExternalAnswerProvider } from './ExternalAnswerContext';
import { LayoutDerivedProvider } from './LayoutDerivedContext';
import type { LayoutNode } from './types';
import { RenderNodes } from './renderNodes';

export interface BespokeLayoutRendererProps extends BespokeFormSectionProps {
  layout: LayoutNode[];
  /** See `hideFieldsMissingFromSchema` on the section context. */
  hideFieldsMissingFromSchema?: boolean;
}

export function BespokeLayoutRenderer({
  layout,
  hideFieldsMissingFromSchema = false,
  ...props
}: BespokeLayoutRendererProps) {
  const schemaMap = useMemo(
    () =>
      buildSectionSchemaMap(
        props.schema,
        props.formSchema,
        hideFieldsMissingFromSchema
      ),
    [props.schema, props.formSchema, hideFieldsMissingFromSchema]
  );

  // useMemo must come before any conditional return (Rules of Hooks).
  const derivedMap = useMemo(
    () =>
      Array.isArray(layout)
        ? computeLayoutDerivedTotals(layout, props.answers, {}, schemaMap)
        : {},
    [layout, props.answers, schemaMap]
  );

  if (!Array.isArray(layout)) {
    return (
      <div className='rounded border border-destructive p-4 text-sm text-destructive'>
        Layout error: layout_json must be a JSON array of nodes.
      </div>
    );
  }

  return (
    <BespokeSectionProvider
      {...props}
      hideFieldsMissingFromSchema={hideFieldsMissingFromSchema}
      schemaMap={schemaMap}
    >
      <LayoutDerivedProvider value={derivedMap}>
        <ExternalAnswerProvider
          value={{
            answers: props.externalAnswers ?? {},
          }}
        >
          <div className='space-y-4'>
            <RenderNodes
              nodes={layout}
              answers={props.answers}
              repeatBindings={{}}
            />
          </div>
        </ExternalAnswerProvider>
      </LayoutDerivedProvider>
    </BespokeSectionProvider>
  );
}
